import { Fragment, memo, useMemo, type ReactNode } from "react";
import { AlertTriangle, Clock, Eye, Loader2 } from "lucide-react";

import { Badge } from "@/modern/components/ui/badge";
import { Card } from "@/modern/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { MIXED_TASK_COLOR } from "@/other/constants/TaskTypes.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import bgContrastHelper from "@/other/utils/bgUtils.jsx";

import {
  getImportExportOperationEyebrow,
  getNotificationCategory,
  getNotificationConfig,
} from "../constants/config";
import {
  resolveCategoryEntity,
} from "../constants/categoryEntityDisplay";
import { TILE_SURFACE_FILL_CLS, toneStyle } from "../constants/tones";
import type { Notification, NotificationConfigContext } from "../types";
import { NotificationActions } from "./NotificationActions";

type TileState = "failed" | "pending" | null;

export type NotificationMosaicLayout = "hero" | "half" | "cell";

/** Strip `**bold**` / `__italic__` markers used for partial description emphasis. */
function stripEmphasisMarkers(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1");
}

/** Render `**…**` as bold and `__…__` as italic; surrounding copy stays regular. */
function renderEmphasizedText(text: string): ReactNode {
  const segments = text.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
  if (segments.length === 1) return text;
  return segments.map((segment, index) => {
    const bold = /^\*\*([^*]+)\*\*$/.exec(segment);
    if (bold) {
      return (
        <strong key={index} className="font-bold">
          {bold[1]}
        </strong>
      );
    }
    const italic = /^__([^_]+)__$/.exec(segment);
    if (italic) {
      return (
        <em key={index} className="italic">
          {italic[1]}
        </em>
      );
    }
    return <Fragment key={index}>{segment}</Fragment>;
  });
}

function formatRelativeTime(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "Recently";
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(timestamp));
}

function contrastColor(background: string): string {
  return bgContrastHelper(background, "#ffffff", "#111111") as string;
}

function trimName(value: string | null | undefined): string | null {
  const name = value?.trim();
  return name || null;
}

function getTaskRecord(taskId: number, context: NotificationConfigContext) {
  return (
    context.project?.getCrudStore?.(STORE_KEYS.taskStore)?.getById?.(taskId) ??
    context.project?.extraTaskStore?.getById?.(taskId) ??
    null
  );
}

function resolveTaskColor(task: { color?: unknown } | null): string | null {
  if (!task) return null;
  const raw = typeof task.color === "string" ? task.color.trim() : "";
  if (!raw || raw === MIXED_TASK_COLOR) return null;
  if (!raw.startsWith("#") && !/^[0-9a-f]{6}$/i.test(raw)) return null;
  return raw.startsWith("#") ? raw : `#${raw}`;
}

function tileState(notification: Notification): TileState {
  if (
    notification.async_task?.status === "Failed" ||
    notification.status === "Failed"
  ) {
    return "failed";
  }
  if (notification.async_task?.status === "Pending") return "pending";
  // Unseen-only inbox — no "Unread" badge (everything here is to mark seen).
  return null;
}

function stripLabelPrefix(raw: string, label: string): string {
  const trimmed = raw.trim();
  const prefix = `${label}:`.toLowerCase();
  if (trimmed.toLowerCase().startsWith(prefix)) {
    return trimmed.slice(label.length + 1).trim() || trimmed;
  }
  return trimmed;
}

function tileTitle(
  notification: Notification,
  context: NotificationConfigContext,
): string {
  const entry = getNotificationConfig(notification);
  const configuredTitle = entry.getTitle(notification, context).trim();
  // Empty config title = description-only tile (skip entity-name shortcuts).
  if (!configuredTitle) return "";

  if (notification.task != null) {
    const task = getTaskRecord(notification.task, context);
    const fromApi = trimName(notification.task_name);
    const fromStore =
      typeof task?.name === "string" ? trimName(task.name) : null;
    const name = fromApi ?? fromStore;
    if (name) return name;
  }
  if (notification.resource != null) {
    const name = trimName(notification.resource_name);
    if (name) return name;
  }
  if (notification.client != null) {
    const name = trimName(notification.client_name);
    if (name) return name;
  }
  if (notification.checklist != null) {
    const name = trimName(notification.checklist_name);
    if (name) return name;
  }

  const withoutLabel = stripLabelPrefix(configuredTitle, entry.label);
  if (withoutLabel && withoutLabel.toLowerCase() !== entry.label.toLowerCase()) {
    return withoutLabel;
  }
  const description = entry.getDescription(notification, context).trim();
  if (description && description.toLowerCase() !== entry.label.toLowerCase()) {
    return description;
  }
  return withoutLabel || configuredTitle || entry.label;
}

function capitalizeSentence(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function tileDescription(
  notification: Notification,
  context: NotificationConfigContext,
  title: string,
): string | null {
  const entry = getNotificationConfig(notification);
  let description = entry.getDescription(notification, context).trim();
  if (!description) return null;

  const normalizedTitle = title.trim().toLowerCase();
  const normalizedLabel = entry.label.toLowerCase();
  let plainDescription = stripEmphasisMarkers(description);
  let normalizedDescription = plainDescription.toLowerCase();

  if (
    normalizedDescription === normalizedTitle ||
    normalizedDescription === normalizedLabel
  ) {
    return null;
  }

  // Drop a leading title/filename so status isn't "file.csv import complete".
  if (normalizedTitle.length > 0 && normalizedDescription.startsWith(normalizedTitle)) {
    description = capitalizeSentence(
      plainDescription.slice(title.trim().length).replace(/^[\s:–—-]+/, ""),
    );
    plainDescription = stripEmphasisMarkers(description);
    normalizedDescription = plainDescription.toLowerCase();
  }

  if (
    !description ||
    normalizedDescription === normalizedTitle ||
    normalizedDescription === normalizedLabel
  ) {
    return null;
  }

  return description;
}

const BADGE_CLS =
  "pointer-events-none h-6 min-h-0 w-fit shrink-0 px-2 py-0 text-sm leading-none font-medium 2xl:h-7 2xl:text-base";

function StateBadge({ state }: { state: TileState }) {
  if (!state) return null;
  if (state === "failed") {
    return (
      <Badge variant="destructive" size="sm" className={BADGE_CLS}>
        Failed
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      size="sm"
      className={cn(
        BADGE_CLS,
        "border-transparent bg-warning text-warning-foreground",
      )}
    >
      In progress
    </Badge>
  );
}

function MarkSeenButton({
  notification,
  context,
}: {
  notification: Notification;
  context: NotificationConfigContext;
}) {
  if (notification.is_seen || notification.async_task?.status === "Pending") {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex size-7 items-center justify-center rounded-lg bg-black/15 opacity-95 transition-[background-color,opacity,transform] duration-150 ease-out hover:bg-black/25 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 active:scale-[0.97] motion-reduce:active:scale-100"
          aria-label="Mark as seen"
          onClick={(event) => {
            event.stopPropagation();
            void Promise.resolve(context.markSeen(notification));
          }}
        >
          <Eye className="size-[1.125rem]" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">Mark as seen</TooltipContent>
    </Tooltip>
  );
}

function ObjectMosaicTile({
  notification,
  context,
  layout = "cell",
  className,
}: {
  notification: Notification;
  context: NotificationConfigContext;
  layout?: NotificationMosaicLayout;
  className?: string;
}) {
  const entry = getNotificationConfig(notification);
  const Icon = entry.icon;
  const entityCue = resolveCategoryEntity(notification, entry.categoryKey);
  // Import/export jobs: operation eyebrow (Import / Export), not the shared
  // section title. Other categories: entity kind when known, else category.
  const categoryLabel =
    getImportExportOperationEyebrow(entry)
    ?? entityCue?.kindLabel
    ?? getNotificationCategory(notification.category)?.label
    ?? getNotificationCategory(entry.categoryKey)?.label
    ?? entry.label;
  const state = tileState(notification);
  const pending = state === "pending";
  const failed = state === "failed";
  const title = tileTitle(notification, context);
  const plainTitle = stripEmphasisMarkers(title);
  const titleHasEmphasis = plainTitle !== title;
  const description = tileDescription(notification, context, plainTitle);
  const plainDescription = description ? stripEmphasisMarkers(description) : null;
  const descriptionHasEmphasis =
    Boolean(description) && plainDescription !== description;
  const task =
    notification.task != null
      ? getTaskRecord(notification.task, context)
      : null;
  const taskColor = resolveTaskColor(task);
  const hexForeground = taskColor ? contrastColor(taskColor) : undefined;
  const categoryStyle = taskColor
    ? undefined
    : toneStyle(entry.classes.bg);

  const actions = useMemo(
    () => entry.getActions(notification, context),
    [context, entry, notification],
  );

  return (
    <Card
      variant="mosaic"
      style={
        taskColor
          ? { backgroundColor: taskColor, color: hexForeground }
          : categoryStyle
      }
      className={cn(
        "group relative flex h-full min-h-0 flex-col overflow-hidden rounded-[1.5rem] p-5 shadow-none",
        !taskColor && TILE_SURFACE_FILL_CLS,
        !taskColor && "text-white",
        className,
      )}
    >
      <div
        className={cn(
          "absolute right-3 top-3 z-10 flex items-center gap-1",
        )}
      >
        <MarkSeenButton notification={notification} context={context} />
        <NotificationActions actions={actions} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-w-0 shrink-0 items-center gap-2 pr-16">
          <span
            className="inline-flex size-5 shrink-0 items-center justify-center opacity-70 [&_svg]:block"
            aria-hidden
          >
            {failed ? (
              <AlertTriangle className="size-5" />
            ) : pending ? (
              <Loader2 className="size-5 animate-spin" />
            ) : entityCue ? (
              <i className={cn(entityCue.iconClass, "text-base leading-none 2xl:text-lg")} />
            ) : (
              <Icon className="size-5" />
            )}
          </span>
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            <p className="m-0 min-w-0 truncate text-sm font-medium uppercase leading-none tracking-wide opacity-80 2xl:text-base">
              {categoryLabel}
            </p>
            <StateBadge state={state} />
          </div>
        </div>

        <div className="mt-2 min-h-0 flex-1 space-y-1 overflow-hidden">
          {plainTitle ? (
            <p
              title={plainTitle}
              className={cn(
                "m-0 min-w-0 break-words leading-snug tracking-tight",
                titleHasEmphasis ? "font-normal" : "font-bold",
                layout === "hero"
                  ? "line-clamp-2 text-2xl 2xl:text-3xl"
                  : layout === "half"
                    ? "line-clamp-2 text-xl 2xl:text-2xl"
                    : "line-clamp-2 text-lg 2xl:text-xl",
              )}
            >
              {titleHasEmphasis ? renderEmphasizedText(title) : title}
            </p>
          ) : null}
          {description ? (
            <div
              title={plainDescription ?? description}
              className={cn(
                "min-w-0 break-words text-base font-normal leading-snug opacity-80 2xl:text-lg",
                layout === "hero" ? "line-clamp-4" : "line-clamp-3",
              )}
            >
              {descriptionHasEmphasis
                ? renderEmphasizedText(description)
                : description}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-auto flex min-w-0 shrink-0 items-center justify-end gap-2.5 pt-3">
        <div className="inline-flex shrink-0 items-center gap-1.5 text-sm font-normal leading-none opacity-70 2xl:text-base">
          <Clock className="size-4" aria-hidden />
          <span className="whitespace-nowrap">
            {formatRelativeTime(notification.alert_date)}
          </span>
        </div>
      </div>
    </Card>
  );
}

export const NotificationTile = memo(function NotificationTile({
  notification,
  context,
  layout,
  className,
}: {
  notification: Notification;
  context: NotificationConfigContext;
  layout?: NotificationMosaicLayout;
  className?: string;
}) {
  return (
    <ObjectMosaicTile
      notification={notification}
      context={context}
      layout={layout}
      className={className}
    />
  );
});
